import Loader from "Common/Loader";
import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { HttpStatus } from "pages/Core/http-status";
import useHasPermission from "hooks/useHasPermission";
import { handleKeyDown } from "Common/Modal/ModalTriggerButton";
import { SubCategoryDataModel } from "../Model/SubCategoryModal";
import { CreateSubCategory, GetCategorySelectList } from "../services/SubCategoryservises";
import notistack from "pages/Core/Notistack";

interface CategoryProp {
  reloadDataSource(): void;
}

const AddSubCategoryModal: React.FC<CategoryProp> = ({ reloadDataSource }) => {
  const hasPermission = useHasPermission();
  const [showCategoryModal, setShowCategoryModal] = React.useState(false);
  const {
    register,
    handleSubmit,
    setFocus,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SubCategoryDataModel>({ mode: "onBlur" });

  const [loading, setLoading] = useState(false);
  const [categoryList, setCategoryList] = useState<{ label: string; value: string }[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchCategories = async () => {
    try {
      const res: any = await GetCategorySelectList();
      if (res.status === HttpStatus.Success) {
        const formatted = res.data.map((c: any) => ({
          text: c.text,
          value: c.value,
        }));
        setCategoryList(formatted);
      } else {
        notistack.notifyFromApi(res);
      }
    } catch (error: any) {
      notistack.error(error.message);
    }
  };

  useEffect(() => {
    if (showCategoryModal) {
      setFocus("name");
      fetchCategories();
    }
  }, [showCategoryModal]);

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      const response: any = await CreateSubCategory(data);
      if (response.status === HttpStatus.Success) {
        notistack.notifyFromApi(response);
        reset();
        setShowCategoryModal(false);
        reloadDataSource();
      } else {
        notistack.notifyFromApi(response);
      }
    } catch (error: any) {
      notistack.error(error.message);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {hasPermission("Category.Write") && (
        <button
          onKeyDown={handleKeyDown}
          onClick={() => {
            setShowCategoryModal(true);
            reset();
          }}
          className="btn btn-sm"
          style={{ border: "1px solid #000" }}
          type="button"
        >
          <i className="bi bi-plus-lg"></i> Add SubCategory{" "}
        </button>
      )}

      <Modal
        show={showCategoryModal}
        onHide={() => {
          setShowCategoryModal(false);
          reset();
        }}
        className="fade"
        dialogClassName="modal-dialog-centered"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add SubCategory</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Select Category <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-control form-select  ${errors.categoryId ? "is-invalid" : ""}`}
                  {...register("categoryId", { required: "Category is required" })}
                >
                  <option value="">-- Select Category --</option>
                  {categoryList.map((cat: any) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.text}
                    </option>
                  ))}
                </select>
                {errors.categoryId && (
                  <div className="text-danger small mt-1">{errors.categoryId.message}</div>
                )}
              </div>

              {/* Input for SubCategory Name */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  SubCategory <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  maxLength={30}
                  placeholder="Enter subcategory"
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  {...register("name", {
                    required: "SubCategory is required",
                    pattern: {
                      value: /^(?!\s)([A-Za-z]+(\s{1,}[A-Za-z]+)*)$/,
                      message:
                        "Only letters and spaces are allowed. No leading or trailing spaces.",
                    },
                  })}
                />
                {errors.name && (
                  <div className="text-danger small mt-1">{errors.name.message}</div>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end align-items-end mt-4">
              <button
                type="button"
                className="btn add-btn me-1"
                onClick={() => {
                  setShowCategoryModal(false);
                  reset();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Add"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AddSubCategoryModal;
